import { motion } from "framer-motion";
import { Fragment } from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  UserCheck,
  PencilRuler,
  ShoppingCart,
  Settings2,
  Truck,
  Receipt,
  Landmark,
  Check,
  ArrowRight,
  Timer,
  Target,
  Eye,
  TrendingUp,
  Sparkles,
  Zap,
  Users,
  BarChart2,
} from "lucide-react";

// Editable workflow-engine block. Renders the numbered stage cards + benefit
// strip. Admin picks an icon per stage/benefit from a curated list so the CMS
// stays typed-safe without shipping the full lucide library to the editor.

const ICONS = {
  FileText,
  UserCheck,
  PencilRuler,
  ShoppingCart,
  Settings2,
  Truck,
  Receipt,
  Landmark,
  Check,
  ArrowRight,
  Timer,
  Target,
  Eye,
  TrendingUp,
  Sparkles,
  Zap,
  Users,
  BarChart2,
};

const COLORS = {
  sky: { bg: "bg-sky-50", ring: "ring-sky-100", icon: "text-sky-600", title: "text-sky-700" },
  violet: { bg: "bg-violet-50", ring: "ring-violet-100", icon: "text-violet-600", title: "text-violet-700" },
  emerald: { bg: "bg-emerald-50", ring: "ring-emerald-100", icon: "text-emerald-600", title: "text-emerald-700" },
  amber: { bg: "bg-amber-50", ring: "ring-amber-100", icon: "text-amber-600", title: "text-amber-700" },
};

function iconFor(name) {
  return ICONS[name] || FileText;
}

function StageCard({ stage, n }) {
  const c = COLORS[stage.color] || COLORS.sky;
  const Icon = iconFor(stage.icon);
  return (
    <div
      className="relative bg-white rounded-2xl ring-1 ring-slate-100 shadow-sm px-3 py-5 sm:px-4 sm:py-6 text-center h-full"
      data-testid={`workflow-stage-${n}`}
    >
      <div
        className="absolute -top-4 left-1/2 -translate-x-1/2 h-8 w-8 rounded-full bg-sky-600 text-white flex items-center justify-center text-sm font-black shadow-md"
        aria-label={`Stage ${n}`}
      >
        {n}
      </div>
      <div className={`mx-auto mt-4 h-12 w-12 rounded-full ${c.bg} ring-1 ${c.ring} flex items-center justify-center`}>
        <Icon className={`h-5 w-5 ${c.icon}`} strokeWidth={2.2} />
      </div>
      <h3 className="mt-3 text-[15px] font-extrabold text-slate-900">{stage.title}</h3>
      <p className="mt-1.5 text-[12.5px] leading-relaxed text-slate-600">{stage.description}</p>
    </div>
  );
}

function StageArrow() {
  return (
    <div className="hidden lg:flex items-center justify-center shrink-0 w-6" aria-hidden>
      <ArrowRight className="h-5 w-5 text-sky-500" strokeWidth={2.4} />
    </div>
  );
}

export default function WorkflowEngineBlock({ data }) {
  const d = data || {};
  const eyebrow = d.eyebrow || "";
  const heading = d.heading || "";
  const subtitle = d.subtitle || "";
  const cardIntro = d.card_intro || "";
  const stages = Array.isArray(d.stages) ? d.stages : [];
  const footerTitle = d.footer_title || "";
  const footerBody = d.footer_body || "";
  const footerCtaLabel = d.footer_cta_label || "";
  const footerCtaHref = d.footer_cta_href || "";
  const benefits = Array.isArray(d.benefits) ? d.benefits : [];

  return (
    <section
      data-testid="workflow-engine-section"
      className="relative bg-white py-16 sm:py-20 lg:py-24"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {eyebrow && (
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.4 }}
            className="text-center text-xs sm:text-sm font-bold tracking-[0.26em] text-sky-600 uppercase"
            data-testid="workflow-engine-eyebrow"
          >
            {eyebrow}
          </motion.p>
        )}

        {heading && (
          <motion.h2
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="mt-3 text-center text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 leading-[1.05]"
            data-testid="workflow-engine-heading"
          >
            {heading}
          </motion.h2>
        )}

        {subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-4 max-w-3xl mx-auto text-center text-base sm:text-lg text-slate-600 leading-relaxed"
            data-testid="workflow-engine-subtitle"
          >
            {subtitle}
          </motion.p>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mt-12 lg:mt-14 rounded-3xl bg-sky-50/60 ring-1 ring-sky-100 px-4 py-8 sm:px-8 sm:py-10 lg:px-10 lg:py-12"
          data-testid="workflow-engine-card"
        >
          {cardIntro && (
            <p className="text-center text-base sm:text-lg font-semibold text-slate-800">
              {cardIntro}
            </p>
          )}

          {stages.length > 0 && (
            <div
              className="mt-8 lg:mt-10 grid grid-cols-2 sm:grid-cols-4 lg:flex lg:flex-row lg:items-stretch gap-y-8 gap-x-3 lg:gap-x-0"
              data-testid="workflow-engine-stages"
            >
              {stages.map((s, i) => (
                <Fragment key={i}>
                  <div className="lg:flex-1 lg:min-w-0">
                    <StageCard stage={s} n={i + 1} />
                  </div>
                  {i < stages.length - 1 && <StageArrow />}
                </Fragment>
              ))}
            </div>
          )}

          {(footerTitle || footerBody || footerCtaLabel) && (
            <div className="mt-10 lg:mt-12 bg-white rounded-2xl ring-1 ring-sky-100 px-5 py-5 sm:px-7 sm:py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
              <div className="flex items-start gap-4">
                <span className="shrink-0 h-11 w-11 rounded-full bg-sky-50 ring-1 ring-sky-100 flex items-center justify-center">
                  <Check className="h-5 w-5 text-sky-600" strokeWidth={2.4} />
                </span>
                <div>
                  {footerTitle && (
                    <p className="text-[15px] sm:text-base font-extrabold text-slate-900">
                      {footerTitle}
                    </p>
                  )}
                  {footerBody && (
                    <p className="mt-1 text-[13px] sm:text-sm text-slate-600 leading-relaxed max-w-2xl">
                      {footerBody}
                    </p>
                  )}
                </div>
              </div>
              {footerCtaLabel && (
                <Link
                  to={footerCtaHref || "/"}
                  data-testid="workflow-engine-learn-btn"
                  className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 font-bold text-sm whitespace-nowrap"
                >
                  {footerCtaLabel}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              )}
            </div>
          )}
        </motion.div>

        {benefits.length > 0 && (
          <div
            className="mt-10 lg:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
            data-testid="workflow-engine-benefits"
          >
            {benefits.map((b, i) => {
              const c = COLORS[b.color] || COLORS.sky;
              const Icon = iconFor(b.icon);
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.45, delay: 0.06 * i }}
                  className="flex items-start gap-3"
                  data-testid={`workflow-engine-benefit-${(b.title || "")
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <span className={`shrink-0 h-11 w-11 rounded-full ${c.bg} ring-1 ${c.ring} flex items-center justify-center`}>
                    <Icon className={`h-5 w-5 ${c.icon}`} strokeWidth={2.2} />
                  </span>
                  <div className="min-w-0">
                    <p className={`text-sm font-extrabold ${c.title}`}>{b.title}</p>
                    <p className="mt-1 text-[13px] text-slate-600 leading-relaxed">{b.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
